import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  ContactsDetailsSaleInvoicesPageDocument,
  FinanceValueObjectsContactTypeEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FeatureAvailabilityType } from 'modules/core/hooks/useFeatureAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { SaleInvoicesListTable } from 'modules/finance/components/Invoices/SaleInvoicesListTable'
import { useArchiveSaleInvoiceDialog } from 'modules/finance/hooks/sale_invoices/useArchiveSaleInvoiceDialog'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

interface ContactsDetailsSaleInvoicesPageProps {
  contactType: FinanceValueObjectsContactTypeEnum
}

const ContactsDetailsSaleInvoicesPage: React.FC<ContactsDetailsSaleInvoicesPageProps> = ({
  contactType,
}) => {
  const { contactId } = useParams()
  const { company, access, employee: currentEmployee, canRequestUpsell } = useCurrentSession()
  const { navigation } = useNavigation()
  const { showArchiveSaleInvoiceDialog } = useArchiveSaleInvoiceDialog()

  const { data, loading, error } = useQuery(ContactsDetailsSaleInvoicesPageDocument, {
    variables: {
      companyId: company.id,
      featureIds: [Features.sales_invoices_creation, Features.sales_invoices_management],
    },
  })

  const hasSaleInvoicesFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.sales_invoices_management
  )?.available

  const createInvoicesFeatureAvailable =
    data?.finance?._policies?.financialDocumentsCreateSale?.can ?? false

  const legalEntitiesOptions =
    data?.companies.legalEntitiesConnection.nodes.map((legalEntity) => ({
      label: legalEntity.legalName || '',
      value: legalEntity.id,
    })) || []

  const featureUsage: FeatureAvailabilityType =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === Features.sales_invoices_creation
    ) ?? {
      available: false,
      id: Features.sales_invoices_creation,
      _internalType: 'feature',
    }

  const availableCurrencies = data
    ? data.apiCore.currenciesConnection.edges.map((edge) => edge.node)
    : []

  const usage = featureUsage.usage || 0
  const usageLimit = featureUsage.usageLimit || -1

  const canSeeSaleInvoicesContent =
    data?.finance?._policies?.financialDocumentsManageSales?.can ?? false
  const isSpanishCompany =
    company.legalEntities.find((legalEntity) => legalEntity.main)?.country === 'es' // Harcoding this country cause we know we dont sell invoices in other countries so upsell wouldnt make sense

  const canSeeSaleInvoicesUpsell = !hasSaleInvoicesFeature && canRequestUpsell && isSpanishCompany
  const canSeePage =
    contactType === 'client' && (canSeeSaleInvoicesContent || canSeeSaleInvoicesUpsell)

  useEffect(() => {
    track(trackingEvents.accounting.viewContactDetailsPage, {
      view: 'sales_invoices',
      sales_invoices_upsell: canSeeSaleInvoicesUpsell,
    })
  }, [canSeeSaleInvoicesUpsell])

  return (
    <ResponsivePage.Body authorized={canSeePage} loading={loading} error={error}>
      {canSeeSaleInvoicesUpsell ? (
        <Box alignItems="center" justifyContent="center" style={{ height: '50vh' }}>
          <UpsellingKit.EmptyState
            moduleRequested={AvailableModules.sales}
            featureRequested={Features.sales_invoices_management}
            requestReason={ValidReasons.MissingAddon}
            requestSource="sales_from_contact_details_on_sales_invoices_tab"
            title={i18n.t('finance.contacts.contact_details.sales_invoices.upsell_title')}
            description={i18n.t(
              'finance.contacts.contact_details.sales_invoices.upsell_description'
            )}
          />
        </Box>
      ) : (
        <SaleInvoicesListTable
          companyId={company.id}
          locale={access.locale}
          legalEntitiesOptions={legalEntitiesOptions}
          availableCurrencies={availableCurrencies}
          showDeleteModalForInvoiceId={(invoiceId) => showArchiveSaleInvoiceDialog({ invoiceId })}
          detailPath={(invoiceId) =>
            navigation.finance.sales.salesInvoices.detail.fullPath({ invoiceId })
          }
          createInvoicesFeatureAvailable={createInvoicesFeatureAvailable}
          usage={usage}
          usageLimit={usageLimit}
          currentEmployeeId={currentEmployee?.id || -1}
          clientId={contactId ? Number(contactId) : undefined}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default ContactsDetailsSaleInvoicesPage
